import React, { useEffect, useState } from 'react';
import { MessageSquare, Bug, Lightbulb, Clock, CheckCircle2, Loader, AlertCircle, Plus } from 'lucide-react';
import { httpClient } from '../services/httpClient';
import { FeedbackModal } from '../components/FeedbackModal';

interface SupportCase {
  id: string;
  caseNumber: string;
  referenceId: string | null;
  subject: string;
  description: string;
  status: string;
  priority: string;
  category: string;
  createdAt: string;
  updatedAt: string;
  resolvedAt: string | null;
  closedAt: string | null;
}

interface MyCasesResponse {
  content: SupportCase[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  OPEN: { label: 'Open', className: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300' },
  IN_PROGRESS: { label: 'In Progress', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' },
  RESOLVED: { label: 'Resolved', className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' },
  CLOSED: { label: 'Closed', className: 'bg-medical-100 text-medical-600 dark:bg-medical-700 dark:text-medical-400' },
};

const CATEGORY_ICON: Record<string, React.ReactNode> = {
  BUG_REPORT: <Bug className="h-4 w-4 text-red-500" />,
  FEATURE_REQUEST: <Lightbulb className="h-4 w-4 text-amber-500" />,
};

export const FeedbackPage: React.FC = () => {
  const [cases, setCases] = useState<SupportCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);

  const fetchCases = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await httpClient.get<MyCasesResponse>('/notifications/feedback/my-cases');
      setCases(response.content);
    } catch (err: any) {
      setError(err.message || 'Failed to load your feedback history.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCases();
  }, []);

  const handleModalClose = () => {
    setIsFeedbackModalOpen(false);
    fetchCases();
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatCategory = (category: string) => {
    if (category === 'BUG_REPORT') return 'Bug Report';
    if (category === 'FEATURE_REQUEST') return 'Feature Request';
    return category;
  };

  return (
    <div className="p-6 max-w-3xl mx-auto dark:bg-medical-900">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-medical-900 dark:text-medical-100 flex items-center space-x-2">
            <MessageSquare className="h-6 w-6" />
            <span>My Feedback</span>
          </h1>
          <p className="text-medical-600 dark:text-medical-400 mt-1">
            Track the status of your bug reports and feature requests.
          </p>
        </div>
        <button
          onClick={() => setIsFeedbackModalOpen(true)}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus className="h-4 w-4" />
          <span>New Report</span>
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader className="h-6 w-6 animate-spin text-primary-500" />
          <span className="ml-3 text-medical-600 dark:text-medical-400">Loading...</span>
        </div>
      ) : error ? (
        <div className="card dark:bg-medical-800 dark:border-medical-700 p-6">
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
              <button onClick={fetchCases} className="text-sm text-primary-600 hover:underline mt-2">
                Try again
              </button>
            </div>
          </div>
        </div>
      ) : cases.length === 0 ? (
        <div className="card dark:bg-medical-800 dark:border-medical-700 p-8 text-center">
          <MessageSquare className="h-12 w-12 text-medical-300 dark:text-medical-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-medical-900 dark:text-medical-100 mb-2">
            No feedback submitted yet
          </h3>
          <p className="text-medical-600 dark:text-medical-400 mb-6">
            Found a bug or have an idea? Let us know and we'll keep you updated.
          </p>
          <button
            onClick={() => setIsFeedbackModalOpen(true)}
            className="btn-primary inline-flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Submit Feedback</span>
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {cases.map((c) => {
            const statusConfig = STATUS_CONFIG[c.status] || STATUS_CONFIG.OPEN;
            return (
              <div
                key={c.id}
                className="card dark:bg-medical-800 dark:border-medical-700 p-5"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 min-w-0">
                    <div className="mt-0.5">
                      {CATEGORY_ICON[c.category] || <MessageSquare className="h-4 w-4 text-medical-400" />}
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-sm font-medium text-medical-900 dark:text-medical-100 truncate">
                        {c.subject}
                      </h3>
                      <div className="flex items-center space-x-3 mt-1">
                        <span className="text-xs text-medical-500 dark:text-medical-400">
                          {c.referenceId || c.caseNumber}
                        </span>
                        <span className="text-xs text-medical-400 dark:text-medical-500">
                          {formatCategory(c.category)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${statusConfig.className}`}>
                    {statusConfig.label}
                  </span>
                </div>

                <div className="mt-3 flex items-center space-x-4 text-xs text-medical-500 dark:text-medical-400">
                  <span className="flex items-center space-x-1">
                    <Clock className="h-3.5 w-3.5" />
                    <span>Submitted {formatDate(c.createdAt)}</span>
                  </span>
                  {c.resolvedAt && (
                    <span className="flex items-center space-x-1">
                      <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                      <span>Resolved {formatDate(c.resolvedAt)}</span>
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <FeedbackModal isOpen={isFeedbackModalOpen} onClose={handleModalClose} />
    </div>
  );
};
